import { FontIcon } from '@fluentui/react';
import { Button, Dialog, Flex, MenuButton, MoreIcon, ProviderConsumer, Text } from '@fluentui/react-northstar';
import { Communication, } from '@fluentui/react-teams';
import { Providers } from '@microsoft/mgt-element/dist/es6/providers/Providers';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Helmet } from 'react-helmet';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { toast } from 'react-toastify';
import ArchiveIcon from '../../svg/archive-icon.svg';
import UnArchiveIcon from '../../svg/Un-archive.svg';
import ConfigCSS from '../config/config.module.css';
import { KBListWrapper } from '../kb/KBListWrapper';
import useCurrentPage from '../kb/useCurrentPage';
import { getCachedFeature } from '../shared/cache/FeatureCache';
import { Breadcrumbs } from '../shared/components/BreadcrumbNavigation';
import { CheckLogin } from '../shared/components/CheckLogin';
import { CheckLicense } from '../shared/components/License/CheckLicense';
import { LoadingScreen } from '../shared/components/LoadingScreen';
import { Toolbar } from '../shared/components/Toolbar';
import { UpgradeNeeded } from '../shared/components/UpgradeNeeded';
import { platformService } from '../shared/services/platform.service';
import {
  ARCHIVE_CONFIRMATION_WARNING,
  ARCHIVE_MODAL_HEADER,
  BUTTONS,
  CONTACT_ADMIN,
  DEFAULT_API_STATUS,
  DONE,
  ENDPOINT_SECURITY_MAPPINGS,
  ERROR,
  LICENSE_FEATURE,
  LOADING,
  REFRESH_SESSION,
  SETTINGS,
  SETTINGS_PAGES,
  SOMETHING_WENT_WRONG,
  toastDefault,
  TREE_LISTING_CONSTANTS,
  UNARCHIVE_CONFIRMATION_SUBTITLE,
  UNARCHIVE_MODAL_HEADER,
  UNAUTHORIZED_ACCESS
} from '../shared/utils/constants';
import { toTitleCase } from '../shared/utils/helper';
import { FORM_HEADERS, FORMS, GETTING_ALL_FORMS, NEW_FORM } from '../templates/CustomForms/constants';
import {
  getSortableTextValue,
  TableSortConfig,
} from '../shared/components/table/tableSortConfig';

export interface Form {
  Id:number,
  Name: string,
  Definition: string,
  IsArchived: boolean,
  IsDefault: boolean,
}

export const SortWithDefaultFormFirst = (formList: any[]) => {
  let forms: Form[] = formList;
    const defaultForm = forms.find(t => t.IsDefault);
    forms = forms.filter(t => !t.IsDefault);
    if (defaultForm!=undefined)
      forms = [defaultForm].concat(forms);
    return forms;
}

export const FormsTable = () => {
  const {t} = useTranslation();
  const history = useHistory();
  const api = new platformService();
  const [data, setData] = useState<RowsData[]>([]);
  const [apiStatus, setApiStatus] = useState<APIStatus>(
    DEFAULT_API_STATUS as APIStatus
  );
  const [isFormFeatureEnabled, setIsFormFeatureEnabled] = useState<boolean>(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState<boolean>(false);
  const [archiveDialogOpen, setArchiveDialogOpen] = useState<boolean>(false);
  const {currentPage, setCurrentPage} = useCurrentPage()
  const [selectedForm, setSelectedForm] = useState<Form>({
    Id:0,
    Name: "",
    Definition: "",
    IsArchived: false,
    IsDefault: false,
  } as Form);
  const formsSortMeta = {
    name: '__sortFormName',
  } as const;

  const formsSortConfig = useMemo((): TableSortConfig<string> => {
    return {
      storageKey: 'settingsFormsTableSort',
      defaultSort: { sortField: 'name', sortDirection: 'asc' },
      columns: [
        {
          field: 'name',
          label: 'name',
          headerKey: 'name',
          getSortValue: (row) => getSortableTextValue(row[formsSortMeta.name]),
        },
      ],
    };
  }, []);

  const fetchData = useCallback(async () => {
    const isArchived = localStorage.getItem('isArchivedFormsView') === 'true'
    setApiStatus({ ...apiStatus, msg: GETTING_ALL_FORMS });
    const check = ENDPOINT_SECURITY_MAPPINGS.find(x => x.key == SETTINGS_PAGES.FORMS);
    if (check && JSON.parse(sessionStorage.getItem('my_application_roles') || '[]').filter(x => x.EndpointPath == check.path && x.Permissions.includes(check.method)).length <= 0) {
      setApiStatus({
        ...apiStatus,
        status: ERROR,
        errCode: 403
      });
      return;
    }
    try {
      const results = await Promise.all([
        api.getForms('?$orderby=Name asc' + (isArchived ? '' : ' &$filter=IsArchived eq false')),
        getCachedFeature(LICENSE_FEATURE.CustomForms)
      ]);
      const featureEnabled = results[1];
      setIsFormFeatureEnabled(featureEnabled);
      let forms = SortWithDefaultFormFirst(results[0].data.value) ?? [];
      if(!featureEnabled){
        forms = forms.filter(form => form.IsDefault);
      }
      const rowData: RowsData[] = forms.map((form: Form) => (setForm(form)));
      setData(rowData);
    } catch (err: any) {
      setApiStatus({
        ...apiStatus,
        status: ERROR,
        errCode: err.response.status
      });
    } finally {
      setApiStatus({ ...apiStatus, status: DONE });
    }
  }, []);

  const setForm = (form: Form) => {
    return {
      id: form['Id'],
      nameSearch: form.Name,
      [formsSortMeta.name]: form.Name ?? '',
      name: (<Flex gap="gap.medium" vAlign="center">
        <Text content={form.Name} />
        {form.IsArchived && (
          <Text content={TREE_LISTING_CONSTANTS.archived} size="smaller" weight="light" disabled />
        )}
      </Flex>
      ),
      "": (
        <div className='flex items-center flex-row-reverse pr-7'>
          <CheckLicense disable>
          <MenuButton
            trigger={
                <Button icon={<MoreIcon />} text iconOnly title="Actions" onClick={(e) => {
                  e.stopPropagation();
                }} />
            }
            menu={{
              items: [
                {
                  content: TREE_LISTING_CONSTANTS.edit,
                  icon: <FontIcon iconName={"Edit"} className={`text-lg`} style={{ lineHeight: "unset" }} />,
                  onClick: (e) => {
                    e.stopPropagation();
                    history.push(`/settings/forms?id=${form.Id}`);
                  }
                },
                {
                  content: form.IsArchived ? TREE_LISTING_CONSTANTS.unArchive : TREE_LISTING_CONSTANTS.archive,
                  icon: form.IsArchived ? <UnArchiveIcon className={`${ConfigCSS.svgIcon}`} /> : <ArchiveIcon className={`${ConfigCSS.svgIcon}`} />,
                  onClick: (e) => {
                    e.stopPropagation();
                    setSelectedForm(form);
                    setArchiveDialogOpen(true);
                  },
                  hidden: form.IsDefault
                },
                {
                  content: TREE_LISTING_CONSTANTS.delete,
                  icon: <FontIcon iconName={"Delete"} className={`text-lg`} style={{ lineHeight: "unset" }} />,
                  onClick: (e) => {
                    e.stopPropagation();
                    setSelectedForm(form);
                    setDeleteDialogOpen(true);
                  },
                  hidden: form.IsDefault
                }
              ],
            }}
          />
          </CheckLicense>
        </div>
      )
    };
  };

  const archiveForm = async (temp: Form) => {
    try {
      const apiData = {
        Id: temp.Id,
        IsArchived: !temp.IsArchived
      };
      await api.updateForm(apiData);
      fetchData();
    } catch (err: any) {
      setApiStatus({
        ...apiStatus,
        status: ERROR,
        errCode: (err.response) ? err.response.status : ""
      });
    }
  };

  const deleteForm = useCallback(async (temp: Form) => {
    try {
      setData((currentRows: RowsData[]) => {
        const rows = currentRows.filter(x => x.id != temp.Id);
        return rows;
      });
      await api.deleteForm(temp.Id);
      toast.success(`Form "${temp.Name}" is deleted.`, toastDefault);
    } catch (err: any) {
      setData((currentRows: RowsData[]) => {
        currentRows.push(setForm(temp));
        return currentRows;
      });
      setApiStatus({
        ...apiStatus,
        status: ERROR,
        errCode: err.response.status
      });
      toast.error(`There was an error deleting Form "${temp.Name}"`, toastDefault);
    } 
  },[]);

  const loadPage = () => {
    fetchData();
  }

  useEffect(() => {

  }, [isFormFeatureEnabled]);

  const navs: Breadcrumbs = {
    breadcrumbs: [
      {
        title: toTitleCase(SETTINGS),
        link: `/${SETTINGS}`
      },
      {
        title: toTitleCase(FORMS),
        link: ''
      }
    ]
  };

  const errConfig = {
    fields: {
      title:
        apiStatus.errCode === 401 || apiStatus.errCode === 403
          ? UNAUTHORIZED_ACCESS
          : SOMETHING_WENT_WRONG,
      desc: CONTACT_ADMIN
    }
  };

  const renderPage = () => {
    if(apiStatus.status === LOADING){
      return <LoadingScreen message={apiStatus.msg} />;
    }else if(apiStatus.status === ERROR){
      return renderErrorPage();
    }else{
      return renderFormTable();
    }
  }

  const renderErrorPage = () => {
    return <ProviderConsumer render={(globalTheme) => (<>
              <Toolbar globalTheme={globalTheme} title="Settings > Forms" hideSave={true} breadcrumbs={navs}/>
              <Flex className={ConfigCSS.configBody}>
                <Flex.Item grow>
                  <>
                    <Communication {...errConfig} />
                    {apiStatus.errCode === 401 && (<Button content= {REFRESH_SESSION} primary onClick={e => { Providers.globalProvider.login(); }} />)}
                  </>
                </Flex.Item>
              </Flex>
            </>)} />;
  }

  const renderFormTable = ()=>{
    return (<>
            <Dialog
              style={{ maxWidth: '500px' }}
              open={archiveDialogOpen}
              onCancel={() => setArchiveDialogOpen(false)}
              cancelButton={toTitleCase(BUTTONS.CANCEL)}
              confirmButton={
                selectedForm?.IsArchived
                  ? TREE_LISTING_CONSTANTS.unArchive
                  : TREE_LISTING_CONSTANTS.archive
              }
              onConfirm={() => { setArchiveDialogOpen(false); archiveForm(selectedForm) }}
              content={`${(!selectedForm.IsArchived) ? ARCHIVE_CONFIRMATION_WARNING : UNARCHIVE_CONFIRMATION_SUBTITLE}`}
              header={`${(!selectedForm.IsArchived) ? ARCHIVE_MODAL_HEADER : UNARCHIVE_MODAL_HEADER} ‘${selectedForm?.Name}’?`}
            />
            <Dialog
                style={{ maxWidth: '500px' }}
                open={deleteDialogOpen}
                onCancel={() => setDeleteDialogOpen(false)}
                cancelButton={toTitleCase(BUTTONS.CANCEL)}
                confirmButton={toTitleCase(BUTTONS.DELETE)}
                onConfirm={async() => { setDeleteDialogOpen(false); deleteForm(selectedForm) }}
                content={t('template.template-delete-message')}
                header={`${t('template.template-form-confirm-delete-header',{ formName: selectedForm?.Name})}`}
              />
              <div style={{ position: "relative" }}>
                <KBListWrapper
                  title={toTitleCase(FORMS)}
                  addNewTitle={isFormFeatureEnabled?toTitleCase(NEW_FORM):""}
                  routeName={SETTINGS_PAGES.FORMS}
                  headers={FORM_HEADERS}
                  rows={data}
                  currentPage={currentPage} 
                  setCurrentPage={setCurrentPage}
                  breadcrumbs={navs}
                  ifEmpty={{
                    header: t('forms.empty-header'),
                    subHeader:
                    t('forms.empty-sub-header')
                  }}
                  archiveViewSwitch
                  archiveViewSwitchState={JSON.parse(localStorage.getItem('isArchivedFormsView') || 'false')}
                  onArchiveSwitchChange={(isArchived: boolean) => {
                    localStorage.setItem('isArchivedFormsView', JSON.stringify(isArchived));
                    fetchData()
                  }}
                sortConfig={formsSortConfig}
                />

                {!isFormFeatureEnabled && (
                  <div style={{position: "absolute", top: "50%", left: "50%", transform: "translate(-50%, -50%)" }}>
                    <UpgradeNeeded
                      headerText={t('ticket.custom-form.no-access')}
                      subheaderText={t('ticket.custom-form.upgrade-plan')}
                    />
                  </div>
                )}
              </div>
          </>
        );
  }
  return (
    <CheckLogin onSignedIn={loadPage}>
      <Helmet>
        <title>{toTitleCase(FORMS)} - Tikit</title>
      </Helmet>
      {
        renderPage()
      }
    </CheckLogin>
  )
};
